"use client";

import React, { useState } from "react";
import type { LegendItem } from "@/lib/map-styles";
import { CaretDown, CaretUp } from "@phosphor-icons/react";

interface MapLegendProps {
  title: string;
  type: "linear" | "quantile";
  items: LegendItem[];
}

export function MapLegend({ title, type, items }: MapLegendProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  if (!items || items.length === 0) return null;

  // Clean title (remove underscores/camelCase)
  const displayTitle = title.replace(/_/g, " ").replace(/([A-Z])/g, " $1").trim();

  return (
    <div className="absolute bottom-8 left-4 z-10 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg border border-gray-200 min-w-[200px] max-w-[260px] transition-all duration-200">
      
      {/* Header / Toggle */}
      <div 
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50 rounded-t-lg"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <h4 className="text-xs font-bold text-slate-700 uppercase truncate pr-2">
          {displayTitle}
        </h4>
        <button className="text-slate-500 hover:text-slate-800">
          {isCollapsed ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
        </button>
      </div>

      {/* Content */}
      {!isCollapsed && (
        <div className="p-3 pt-0">
          {type === "linear" ? (
            <div className="flex flex-col gap-1">
              {/* Gradient Bar */}
              <div 
                className="h-3 w-full rounded-sm"
                style={{
                  background: `linear-gradient(to right, ${items[0].color}, ${items[items.length-1].color})`
                }}
              />
              <div className="flex justify-between text-[10px] text-slate-600 font-medium">
                  <span>{items[0].label}</span>
                  <span>{items[items.length-1].label}</span>
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              {items.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-sm border border-slate-300 shadow-sm flex-shrink-0" 
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-[10px] text-slate-600 font-medium truncate">
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
